import { pct } from '@shared/format';
import type { BoomReason, GameStatus, Role, Stars } from '@shared/types';
import { t } from '../i18n/ru';

/** Всё, что попадает на картинку результата. */
export interface CardData {
  title: string;
  status: GameStatus;
  heat: number;
  stars: Stars;
  boom: BoomReason | null;
  roles: readonly Role[];
  /** Адрес игры внизу карточки («telepathy.excalibolt.com»). */
  host: string;
}

interface Gradient {
  addColorStop(offset: number, color: string): void;
}
/**
 * Подмножество CanvasRenderingContext2D, которое нужно карточке. Свой интерфейс — чтобы код (и его юнит-тесты
 * в Node без DOM-типов) не зависел от lib.dom; настоящий контекст ему подходит.
 */
export interface Ctx {
  fillStyle: string | Gradient | object;
  strokeStyle: string | Gradient | object;
  font: string;
  textAlign: string;
  textBaseline: string;
  lineWidth: number;
  lineCap: string;
  shadowColor: string;
  shadowBlur: number;
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): Gradient;
  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number): Gradient;
  measureText(text: string): { width: number };
  fillText(text: string, x: number, y: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arcTo(x1: number, y1: number, x2: number, y2: number, r: number): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  fill(): void;
  stroke(): void;
}

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1350;

const DISPLAY = '"Unbounded Variable", "Onest Variable", system-ui, sans-serif';
const BODY = '"Onest Variable", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
/** Шрифты, которые нужно успеть загрузить до рисования (иначе canvas нарисует запасным). */
export const CARD_FONTS = [`700 56px ${DISPLAY}`, `600 40px ${DISPLAY}`, `600 40px ${BODY}`] as const;

const TILE: Record<Role, string> = { t: '#3af0a5', d: '#a29ecb', b: '#ff5573' };
const GOLD = '#ffbb3d';
const TILE_SIZE = 64;
const TILE_GAP = 12;
const PER_ROW = 11;

function roundedRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Многолучевая звезда: spikes = 5 — обычная звезда, 14 — «взрыв». */
function starPath(ctx: Ctx, cx: number, cy: number, outer: number, inner: number, spikes: number) {
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / spikes - Math.PI / 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Уменьшает размер шрифта, пока текст не влезет в ширину: названия полей бывают длинными. */
function fitFont(ctx: Ctx, text: string, weight: number, max: number, width: number) {
  let size = max;
  ctx.font = `${weight} ${size}px ${DISPLAY}`;
  while (size > 30 && ctx.measureText(text).width > width) {
    size -= 2;
    ctx.font = `${weight} ${size}px ${DISPLAY}`;
  }
}

function glow(ctx: Ctx, x: number, y: number, r: number, color: string) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

function drawStars(ctx: Ctx, cx: number, cy: number, stars: number) {
  for (let n = 0; n < 3; n++) {
    const x = cx + (n - 1) * 190;
    starPath(ctx, x, cy, 84, 36, 5);
    if (n < stars) {
      ctx.shadowColor = 'rgba(255,187,61,0.8)';
      ctx.shadowBlur = 40;
      ctx.fillStyle = GOLD;
      ctx.fill();
      ctx.shadowBlur = 0;
    } else {
      ctx.lineWidth = 6;
      ctx.strokeStyle = 'rgba(255,255,255,0.28)';
      ctx.stroke();
    }
  }
}

/** Бомба на вспышке — для проигрыша. */
function drawBoom(ctx: Ctx, cx: number, cy: number) {
  starPath(ctx, cx, cy, 118, 78, 12);
  const g = ctx.createLinearGradient(cx, cy - 130, cx, cy + 130);
  g.addColorStop(0, GOLD);
  g.addColorStop(1, '#ff5573');
  ctx.shadowColor = 'rgba(255,85,115,0.8)';
  ctx.shadowBlur = 50;
  ctx.fillStyle = g;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#1b1035';
  ctx.beginPath();
  ctx.arc(cx - 6, cy + 12, 52, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = 9;
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#1b1035';
  ctx.beginPath();
  ctx.moveTo(cx + 26, cy - 22);
  ctx.lineTo(cx + 50, cy - 48);
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(cx + 54, cy - 54, 9, 0, Math.PI * 2);
  ctx.fill();
}

function drawHeatBar(ctx: Ctx, cx: number, y: number, heat: number) {
  const w = 720;
  const h = 26;
  const x = cx - w / 2;
  roundedRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fill();
  const fill = Math.min(1, Math.max(0, heat)) * w;
  if (fill > 0) {
    const g = ctx.createLinearGradient(x, 0, x + w, 0);
    g.addColorStop(0, '#35e3c0');
    g.addColorStop(0.38, '#ffd23f');
    g.addColorStop(0.66, '#ff8a3a');
    g.addColorStop(1, '#ff3e5e');
    roundedRect(ctx, x, y, Math.max(fill, h), h, h / 2);
    ctx.fillStyle = g;
    ctx.fill();
  }
}

/** Плитки ходов: зелёная — своё слово, серая — обманка, красная — бомба. Ряды центрируются. */
function drawRecap(ctx: Ctx, cx: number, top: number, roles: readonly Role[]) {
  const rows = Math.ceil(roles.length / PER_ROW);
  for (let r = 0; r < rows; r++) {
    const row = roles.slice(r * PER_ROW, (r + 1) * PER_ROW);
    const rowW = row.length * TILE_SIZE + (row.length - 1) * TILE_GAP;
    row.forEach((role, k) => {
      roundedRect(
        ctx,
        cx - rowW / 2 + k * (TILE_SIZE + TILE_GAP),
        top + r * (TILE_SIZE + TILE_GAP),
        TILE_SIZE,
        TILE_SIZE,
        18,
      );
      ctx.fillStyle = TILE[role];
      ctx.fill();
    });
  }
}

/**
 * Рисует карточку результата 1080×1350 (формат ленты и историй). Только canvas-примитивы, без эмодзи:
 * на разных платформах эмодзи выглядят по-разному, а карточка должна везде быть одинаковой.
 */
export function drawShareCard(ctx: Ctx, card: CardData): void {
  const cx = CARD_WIDTH / 2;
  const win = card.status === 'win';

  const bg = ctx.createLinearGradient(0, 0, 0, CARD_HEIGHT);
  bg.addColorStop(0, '#1a1246');
  bg.addColorStop(1, '#0a0820');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
  glow(ctx, 120, 60, 700, 'rgba(112,66,255,0.55)');
  glow(ctx, 1000, 520, 560, win ? 'rgba(255,64,178,0.32)' : 'rgba(255,46,86,0.5)');
  glow(ctx, 260, 1290, 620, win ? 'rgba(24,170,255,0.3)' : 'rgba(255,138,58,0.3)');

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Бренд
  const brand = ctx.createLinearGradient(cx - 200, 0, cx + 200, 0);
  brand.addColorStop(0, '#7b5cff');
  brand.addColorStop(0.5, '#c15bff');
  brand.addColorStop(1, '#ff5fb5');
  ctx.fillStyle = brand;
  ctx.font = `700 56px ${DISPLAY}`;
  ctx.fillText(t.appName.toUpperCase(), cx, 120);

  // Название поля
  ctx.fillStyle = 'rgba(244,242,255,0.6)';
  ctx.font = `600 34px ${BODY}`;
  ctx.fillText(t.card.board, cx, 230);
  ctx.fillStyle = '#f4f2ff';
  fitFont(ctx, card.title, 700, 76, 920);
  ctx.fillText(card.title, cx, 322);

  // Главный результат
  if (win) drawStars(ctx, cx, 520, card.stars);
  else drawBoom(ctx, cx, 505);
  ctx.fillStyle = win ? '#f4f2ff' : '#ff8ea1';
  fitFont(ctx, win ? (card.stars === 3 ? t.card.perfect : t.card.win) : t.card.lose, 700, 56, 940);
  ctx.fillText(win ? (card.stars === 3 ? t.card.perfect : t.card.win) : t.card.lose, cx, 704);

  // Шкала бомбы: у победы — итог, у поражения от перегрева — полная
  const showBar = win || card.boom === 'heat';
  if (showBar) {
    drawHeatBar(ctx, cx, 760, win ? card.heat : 1);
    ctx.fillStyle = 'rgba(244,242,255,0.75)';
    ctx.font = `600 36px ${BODY}`;
    ctx.fillText(win ? t.card.heat(pct(card.heat)) : t.card.overheated, cx, 848);
  } else {
    ctx.fillStyle = 'rgba(244,242,255,0.75)';
    ctx.font = `600 36px ${BODY}`;
    ctx.fillText(t.card.picked, cx, 790);
  }

  // Ходы
  ctx.fillStyle = 'rgba(244,242,255,0.6)';
  ctx.font = `600 32px ${BODY}`;
  ctx.fillText(t.turnsCount(card.roles.length), cx, 940);
  drawRecap(ctx, cx, 972, card.roles);

  // Призыв и адрес
  ctx.fillStyle = '#f4f2ff';
  ctx.font = `700 44px ${DISPLAY}`;
  ctx.fillText(t.card.cta, cx, 1216);
  ctx.fillStyle = brand;
  ctx.font = `600 40px ${BODY}`;
  ctx.fillText(card.host, cx, 1282);
}
